import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClub } from '../App';
import api from '../utils/api';
import { toast } from 'sonner';
import { Trophy, TrendingUp, CheckCircle2, XCircle, ArrowLeft, Activity, Camera, MapPin, Clock } from 'lucide-react';
import { RatingBadge } from '../components/RatingBadge';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

function RatingChart({ playerId }) {
  const { selectedClub } = useClub();
  const [scope, setScope] = useState('lifetime');
  const [scopeId, setScopeId] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [clubs, setClubs] = useState([]);

  useEffect(() => {
    // Fetch clubs and tournaments for dropdown
    if (selectedClub) {
      api.get(`/tournaments?group_id=${selectedClub.id}`).then(r => setTournaments(r.data)).catch(() => {});
    }
    api.get('/clubs').then(r => setClubs(r.data)).catch(() => {});
  }, [selectedClub]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ scope });
    if (scopeId) params.set('scope_id', scopeId);
    api.get(`/players/${playerId}/rating-history?${params}`)
      .then(r => setHistory(r.data.history || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [playerId, scope, scopeId]);

  const chartData = history.map((h, i) => ({
    game: i + 1,
    lifetime: h.lifetime_rating,
    club: h.club_rating,
    tournament: h.tournament_rating,
    result: h.result,
  }));

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="rating-chart-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-base font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <TrendingUp size={16} className="text-green-600" /> Rating Progress
        </h2>
        <div className="flex items-center gap-2">
          <select value={scope} onChange={e => { setScope(e.target.value); setScopeId(''); }}
            className="h-8 rounded-lg border border-slate-200 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
            data-testid="chart-scope-select">
            <option value="lifetime">Lifetime</option>
            <option value="club">By Club</option>
            <option value="tournament">By Tournament</option>
          </select>
          {scope === 'club' && (
            <select value={scopeId} onChange={e => setScopeId(e.target.value)}
              className="h-8 rounded-lg border border-slate-200 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid="chart-club-select">
              <option value="">Select club...</option>
              {clubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {scope === 'tournament' && (
            <select value={scopeId} onChange={e => setScopeId(e.target.value)}
              className="h-8 rounded-lg border border-slate-200 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid="chart-tournament-select">
              <option value="">Select tournament...</option>
              {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
        </div>
      </div>
      {loading ? (
        <div className="h-48 flex items-center justify-center text-sm text-slate-400">Loading...</div>
      ) : chartData.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-sm text-slate-400">
          No rating history yet. Play some games to see your progress!
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="game" tick={{ fontSize: 10, fill: '#94a3b8' }} label={{ value: 'Game', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#94a3b8' }} />
            <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} domain={['dataMin - 30', 'dataMax + 30']} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
              formatter={(val, name) => [val, name === 'lifetime' ? 'Lifetime' : name === 'club' ? 'Club' : 'Tournament']} />
            <Line type="monotone" dataKey="lifetime" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3, fill: '#8b5cf6' }} name="lifetime" />
            <Line type="monotone" dataKey="club" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: '#3b82f6' }} name="club" />
            {scope === 'tournament' && <Line type="monotone" dataKey="tournament" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: '#f59e0b' }} name="tournament" />}
          </LineChart>
        </ResponsiveContainer>
      )}
      <div className="flex items-center justify-center gap-4 mt-2 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><div className="w-3 h-0.5 bg-purple-500 rounded" /> Lifetime</span>
        <span className="flex items-center gap-1"><div className="w-3 h-0.5 bg-blue-500 rounded" /> Club</span>
        {scope === 'tournament' && <span className="flex items-center gap-1"><div className="w-3 h-0.5 bg-amber-500 rounded" /> Tournament</span>}
      </div>
    </div>
  );
}

function StatBadge({ label, value, highlight }) {
  return (
    <div className={`rounded-xl p-4 text-center border ${highlight ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}>
      <p className={`text-2xl font-bold ${highlight ? 'text-green-700' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
        {value}
      </p>
      <p className={`text-xs font-medium mt-0.5 ${highlight ? 'text-green-600' : 'text-slate-500'}`}>{label}</p>
    </div>
  );
}

export default function PlayerProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const canManage = user?.role === 'admin' || user?.role === 'contributor';

  const fetchPlayer = () => {
    api.get(`/players/${id}`)
      .then(res => setPlayer(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Player not found'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPlayer(); }, [id]);

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/players/${id}/picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Profile picture updated!');
      fetchPlayer();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload picture');
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="h-32 bg-slate-100 rounded-xl animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto text-center py-16">
        <p className="text-red-500 font-medium">{error}</p>
        <Link to="/players" className="text-sm text-green-600 hover:text-green-700 mt-3 inline-block">← Back to Players</Link>
      </div>
    );
  }

  const winRate = player.total_games > 0 ? Math.round(player.total_wins / player.total_games * 100) : 0;
  const isOwnProfile = user?.player_id === id;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Back */}
      {(user?.role === 'admin' || user?.role === 'contributor') && (
        <Link to="/players" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors">
          <ArrowLeft size={14} /> Back to Players
        </Link>
      )}

      {/* Profile header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="player-profile-card">
        <div className="h-20 bg-gradient-to-r from-green-600 to-green-500" />
        <div className="px-6 pb-6">
          <div className="-mt-8 mb-4 relative w-fit">
            {player.picture_url ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${player.picture_url}`}
                alt={player.name}
                className="w-16 h-16 rounded-xl border-4 border-white shadow-md object-cover"
                data-testid="player-profile-picture"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-white border-4 border-white shadow-md flex items-center justify-center text-2xl font-bold text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {player.name?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            {canManage && (
              <>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-600 hover:bg-green-700 text-white rounded-full flex items-center justify-center shadow transition-all"
                  data-testid="upload-picture-btn"
                  title="Upload picture"
                >
                  <Camera size={12} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePictureUpload}
                  className="hidden"
                  data-testid="picture-file-input"
                />
              </>
            )}
          </div>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{player.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {isOwnProfile && (
                  <span className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-medium">
                    Your Profile
                  </span>
                )}
                {(player.country || player.state || player.city) && (
                  <span className="text-xs text-slate-500 flex items-center gap-1" data-testid="player-location">
                    <MapPin size={11} /> {[player.city, player.state, player.country].filter(Boolean).join(', ')}
                  </span>
                )}
                {player.availability_status && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
                    player.availability_status === 'available' ? 'bg-green-50 text-green-700 border-green-200' :
                    player.availability_status === 'busy' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    'bg-slate-100 text-slate-500 border-slate-200'
                  }`} data-testid="player-availability-badge">
                    {player.availability_status.charAt(0).toUpperCase() + player.availability_status.slice(1)}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Lifetime Rating</p>
              <p className="text-3xl font-bold text-slate-900 font-mono" data-testid="lifetime-rating">{player.lifetime_rating}</p>
              <div className="mt-1"><RatingBadge status={player.rating_status} /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="player-stats-grid">
        <StatBadge label="Games Played" value={player.total_games} />
        <StatBadge label="Wins" value={player.total_wins} highlight />
        <StatBadge label="Losses" value={player.total_losses} />
        <StatBadge label="Win Rate" value={`${winRate}%`} highlight={winRate >= 50} />
      </div>

      {/* Rating Progress Chart */}
      <RatingChart playerId={id} />

      {/* Availability & Location detail */}
      {(player.availability_status || (player.available_days && player.available_days.length > 0) || player.available_time_start) && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="player-availability-section">
          <h2 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Clock size={16} /> Availability
          </h2>
          <div className="flex flex-wrap gap-4 text-sm">
            {player.available_days && player.available_days.length > 0 && (
              <div>
                <p className="text-xs text-slate-400 font-medium mb-1">Days</p>
                <div className="flex gap-1">
                  {['mon','tue','wed','thu','fri','sat','sun'].map(d => (
                    <span key={d} className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-medium ${
                      player.available_days.includes(d) ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-slate-50 text-slate-300 border border-slate-100'
                    }`}>{d.charAt(0).toUpperCase() + d.slice(1, 3)}</span>
                  ))}
                </div>
              </div>
            )}
            {(player.available_time_start || player.available_time_end) && (
              <div>
                <p className="text-xs text-slate-400 font-medium mb-1">Time</p>
                <p className="text-sm text-slate-700 font-mono">
                  {player.available_time_start || '--:--'} – {player.available_time_end || '--:--'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Games */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="recent-games-section">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Recent Games</h2>
        </div>
        {player.recent_games.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <Activity className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">No games played yet</p>
          </div>
        ) : (
          player.recent_games.map((g, i) => (
            <div key={g.id} className={`px-6 py-4 flex items-center gap-4 ${i < player.recent_games.length - 1 ? 'border-b border-slate-100' : ''}`} data-testid={`recent-game-${g.id}`}>
              {g.result === 'win' ? (
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">
                  <span className="text-slate-700">{g.team1_players.join(' & ')}</span>
                  <span className="text-slate-400 mx-2">vs</span>
                  <span className="text-slate-700">{g.team2_players.join(' & ')}</span>
                </p>
                <p className="text-xs text-slate-400 mt-0.5">{g.tournament_name}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-mono font-bold text-slate-900">
                  {g.team1_score} – {g.team2_score}
                </p>
                <span className={`text-xs font-semibold ${g.result === 'win' ? 'text-green-600' : 'text-red-500'}`}>
                  {g.result === 'win' ? 'WIN' : 'LOSS'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Standings quick link */}
      <div className="flex gap-4">
        <Link
          to="/standings"
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-all"
          data-testid="view-standings-link"
        >
          <Trophy size={15} /> View Lifetime Standings
        </Link>
      </div>
    </div>
  );
}
